import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import { pinned, WRAP_ANYWHERE } from '../../../../ui/components/pure/list-table/list-table-column-styles';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { KioskState } from '../types/kiosk.types';
import { formatKioskStatus, calculateOfflineDuration, isKioskActive, formatTimestamp } from '../utils/kiosk.helpers';
import { Chip } from '@mui/material';
import { Colors } from '../../../../ui/theme/colors';

// Project, hub and kiosk ID are variable text and share the spare width; the rest are fixed-format
// values, so they keep one width at every screen size.
const FIXED_COLUMN_WIDTHS = {
  softwareVersion: '150px',
  operatingSystem: '160px',
  status: '120px',
  lastSeen: '180px',
  offlineDuration: '160px'
} as const;

export function useKioskTable(kioskIdToHubName?: Map<string, string>, kioskIdToProjectName?: Map<string, string>) {
  const tb = initTable<KioskState>();
  tb.styles({
    table: { tableLayout: 'fixed' }
  });
  tb.rowStyles({ td: { height: '64px' } });

  // Project Name column
  tb.column('PROJECT NAME', TableComponents.Text, {
    value: (row) => kioskIdToProjectName?.get(row.id) || 'Unknown project'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: 'project name',
    styles: WRAP_ANYWHERE
  });

  // Hub Name column
  tb.column('HUB NAME', TableComponents.Text, {
    value: (row) => kioskIdToHubName?.get(row.id) || 'Unknown hub'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: 'hub name',
    styles: WRAP_ANYWHERE
  });

  // Kiosk ID/Name column
  tb.column('KIOSK ID', TableComponents.Text, { value: ':id' }).options({
    isSortable: false,
    sortId: 'id',
    minBreakpoint: 'md',
    tooltip: 'kiosk identifier',
    styles: WRAP_ANYWHERE
  });

  // Software Version column
  tb.column('SOFTWARE VERSION', TableComponents.Text, {
    value: (row) => row.softwareVersion || '-'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: 'kiosk app software version',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.softwareVersion)
  });

  // Operating System column
  tb.column('OPERATING SYSTEM', TableComponents.Text, {
    value: (row) => row.osName || '-'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: 'kiosk operating system name',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.operatingSystem)
  });

  // Connection Status column
  function StatusChip({ row }: { row: KioskState }) {
    const isActive = isKioskActive(row.connectionState);
    const statusLabel = formatKioskStatus(row.connectionState);
    return (
      <Chip
        label={statusLabel}
        size="small"
        style={{
          backgroundColor: isActive ? Colors.collectedGreen : Colors.cancelledDeletedRed,
          color: 'white',
          fontWeight: 'bold'
        }}
      />
    );
  }

  tb.column('STATUS', StatusChip, { row: (row) => row }).options({
    isSortable: false,
    sortId: 'connectionState',
    minBreakpoint: 'md',
    tooltip: 'connection status',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.status)
  });

  // Last Seen column
  tb.column('LAST SEEN', TableComponents.Text, {
    value: (row) => formatTimestamp(row.lastUpdated)
  }).options({
    isSortable: false,
    sortId: 'lastUpdated',
    minBreakpoint: 'md',
    tooltip: 'last seen timestamp',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.lastSeen)
  });

  // Offline Duration column (only for inactive kiosks)
  tb.column('OFFLINE DURATION', TableComponents.Text, {
    value: (row) => {
      if (isKioskActive(row.connectionState)) {
        return '—';
      }
      return calculateOfflineDuration(row.lastUpdated);
    }
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: 'time since last connection',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.offlineDuration)
  });

  // Mobile column group
  function MobileStatusChip({ row }: { row: KioskState }) {
    const isActive = isKioskActive(row.connectionState);
    const statusLabel = formatKioskStatus(row.connectionState);
    return (
      <Chip
        label={statusLabel}
        size="small"
        style={{
          backgroundColor: isActive ? Colors.collectedGreen : Colors.cancelledDeletedRed,
          color: 'white',
          fontWeight: 'bold'
        }}
      />
    );
  }

  tb.columnGroup('KIOSK')
    .row(TableComponents.Text, { value: (row) => kioskIdToHubName?.get(row.id) || 'Unknown hub' })
    .row(TableComponents.Text, { value: ':id' })
    .row(TableComponents.Text, { value: (row) => row.osName || '-' })
    .row(MobileStatusChip, { row: (row) => row })
    .options({
      isSortable: false,
      maxBreakpoint: 'sm'
    });

  tb.columnGroup('')
    .row(TableComponents.Text, { value: (row) => formatTimestamp(row.lastUpdated) })
    .row(TableComponents.Text, {
      value: (row) => {
        if (isKioskActive(row.connectionState)) {
          return '—';
        }
        return calculateOfflineDuration(row.lastUpdated);
      }
    })
    .options({
      isSortable: false,
      maxBreakpoint: 'sm',
      styles: { display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }
    });

  return tb;
}
